import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'

import { findFirstAllowedPath } from '@/features/rbac/pathPermissions'
import { RouteGuard } from '@/features/rbac/RouteGuard'
import { devRoutes } from '@/routes/devRoutes'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary'
import { Spinner } from '@/shared/components/atoms/Spinner/Spinner'
import MainLayout from '@/shared/components/layouts/MainLayout'
import {
  selectIsSuperAdmin,
  selectPermissions,
} from '@/store/slices/authSlice'

const RootLanding = () => {
  const permissions = useSelector(selectPermissions)
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  const landing = findFirstAllowedPath(permissions, isSuperAdmin) ?? '/403'
  return <Navigate to={landing} replace />
}

const ShopProductEditRedirect = () => {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/gamification/shop/products/${id}`} replace />
}

// Lazy load pages
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const DashboardPage = lazy(
  () => import('@/features/dashboard/pages/DashboardPage')
)
const PaymentsPage = lazy(
  () => import('@/features/payments/pages/PaymentsPage')
)

// Payment Subscriptions pages
const SubscriptionListPage = lazy(
  () => import('@/features/payment-subscriptions/pages/SubscriptionListPage')
)
const TammiOrdersPage = lazy(
  () => import('@/features/tammi-payments/pages/TammiOrdersPage')
)

// User Management pages
const UserManagementUsersListPage = lazy(
  () => import('@/features/user-management/pages/UsersListPage')
)
const UserManagementCreateUserPage = lazy(
  () => import('@/features/user-management/pages/CreateUserPage')
)
const UserManagementEditUserPage = lazy(
  () => import('@/features/user-management/pages/EditUserPage')
)
const UserManagementUserDetailPage = lazy(
  () => import('@/features/user-management/pages/UserDetailPage')
)
const UserManagementFeedbacksListPage = lazy(
  () => import('@/features/user-management/pages/FeedbacksListPage')
)

// Admin Management pages
const AdminManagementAdminsListPage = lazy(
  () => import('@/features/admin-management/pages/AdminsListPage')
)

// Settings page (self-service profile + password change)
const SettingsPage = lazy(
  () => import('@/features/settings/pages/SettingsPage')
)

// GameFi pages
const MissionDefinitionsListPage = lazy(
  () => import('@/features/gamefi/pages/MissionDefinitionsListPage')
)
const MissionDefinitionCreatePage = lazy(
  () => import('@/features/gamefi/pages/MissionDefinitionCreatePage')
)
const MissionDefinitionDetailPage = lazy(
  () => import('@/features/gamefi/pages/MissionDefinitionDetailPage')
)
const UserMonitoringPage = lazy(
  () => import('@/features/gamefi/pages/UserMonitoringPage')
)
const DailyStreakRewardsConfigPage = lazy(
  () => import('@/features/gamefi/pages/DailyStreakRewardsConfigPage')
)

// Gamification (new modules)
const AppEventsPage = lazy(
  () => import('@/features/gamification/app-events/pages/AppEventsPage')
)
const AppEventFormPage = lazy(
  () => import('@/features/gamification/app-events/pages/AppEventFormPage')
)
const AppEventStatsPage = lazy(
  () => import('@/features/gamification/app-events/pages/AppEventStatsPage')
)
const ShopCategoriesPage = lazy(
  () =>
    import('@/features/gamification/shop-categories/pages/ShopCategoriesPage')
)
const ShopProductsPage = lazy(
  () => import('@/features/gamification/shop-products/pages/ShopProductsPage')
)
const ShopProductCreatePage = lazy(
  () =>
    import('@/features/gamification/shop-products/pages/ShopProductCreatePage')
)
const ShopProductDetailPage = lazy(
  () =>
    import('@/features/gamification/shop-products/pages/ShopProductDetailPage')
)
// Voucher do QLBH (HTVHKD) quản lý; route bên dưới đã tắt nên import này
// cũng tắt theo, bỏ comment cả hai khi cần bật lại.
// const VouchersPage = lazy(
//   () => import('@/features/gamification/vouchers/pages/VouchersPage')
// )
const RedemptionPage = lazy(
  () => import('@/features/gamification/redemptions/pages/RedemptionPage')
)
const PostHogEventsPage = lazy(
  () => import('@/features/analytics/pages/PostHogEventsPage')
)
const GameItemsPage = lazy(
  () => import('@/features/gamification/game-items/pages/GameItemsPage')
)
const TreeCareActionConfigsPage = lazy(
  () =>
    import(
      '@/features/gamification/tree-care-action-configs/pages/TreeCareActionConfigsPage'
    )
)
const TreeGrowthStageConfigsPage = lazy(
  () =>
    import(
      '@/features/gamification/tree-growth-stage-configs/pages/TreeGrowthStageConfigsPage'
    )
)
const TreeHarvestRewardsConfigsPage = lazy(
  () =>
    import(
      '@/features/gamification/tree-harvest-rewards-configs/pages/TreeHarvestRewardsConfigsPage'
    )
)
const MissionItemRewardsConfigsPage = lazy(
  () =>
    import(
      '@/features/gamification/mission-item-rewards-configs/pages/MissionItemRewardsConfigsPage'
    )
)

// Survey pages
const SurveyTopicsListPage = lazy(
  () => import('@/features/survey/pages/SurveyTopicsListPage')
)
const SurveyTopicCreatePage = lazy(
  () => import('@/features/survey/pages/SurveyTopicCreatePage')
)
const SurveyTopicDetailPage = lazy(
  () => import('@/features/survey/pages/SurveyTopicDetailPage')
)

// Survey Question pages
const SurveyQuestionsListPage = lazy(
  () => import('@/features/survey-question/pages/SurveyQuestionsListPage')
)
const SurveyQuestionCreatePage = lazy(
  () => import('@/features/survey-question/pages/SurveyQuestionCreatePage')
)
const SurveyQuestionDetailPage = lazy(
  () => import('@/features/survey-question/pages/SurveyQuestionDetailPage')
)

// Reports & Analytics pages
const BIDashboardPage = lazy(
  () => import('@/features/reports/pages/BIDashboardPage')
)
const CustomReportsPage = lazy(
  () => import('@/features/reports/pages/CustomReportsPage')
)
const ScheduledReportsPage = lazy(
  () => import('@/features/reports/pages/ScheduledReportsPage')
)
const AnalyticsPage = lazy(
  () => import('@/features/reports/pages/AnalyticsPage')
)
const PredictiveAnalyticsPage = lazy(
  () => import('@/features/reports/pages/PredictiveAnalyticsPage')
)

// System Configuration pages
const SystemConfigIndexPage = lazy(
  () => import('@/features/system-config/pages/SystemConfigIndexPage')
)
// const FeatureFlagsPage = lazy(() => import('@/features/system-config/pages/FeatureFlagsPage')) // Not supported by API
const AppSettingsPage = lazy(
  () => import('@/features/system-config/pages/AppSettingsPage')
)
const AppSettingCreatePage = lazy(
  () => import('@/features/system-config/pages/AppSettingCreatePage')
)
const AppSettingDetailPage = lazy(
  () => import('@/features/system-config/pages/AppSettingDetailPage')
)
const NotificationTemplatesPage = lazy(
  () => import('@/features/system-config/pages/NotificationTemplatesPage')
)
const LocalizationKeysPage = lazy(
  () => import('@/features/system-config/pages/LocalizationKeysPage')
)
const SystemAppConfigsPage = lazy(
  () => import('@/features/system-config/pages/SystemAppConfigsPage')
)
const TTSVoicesConfigPage = lazy(
  () => import('@/features/system-config/pages/TTSVoicesConfigPage')
)
const TTSVoiceCreatePage = lazy(
  () => import('@/features/system-config/pages/TTSVoiceCreatePage')
)
const TTSVoiceDetailPage = lazy(
  () => import('@/features/system-config/pages/TTSVoiceDetailPage')
)
// const MaintenanceModePage = lazy(() => import('@/features/system-config/pages/MaintenanceModePage')) // Not supported by API

// Content Management pages
const LegalDocumentsPage = lazy(
  () => import('@/features/content-management/pages/LegalDocumentsPage')
)
const LocationManagementPage = lazy(
  () => import('@/features/content-management/pages/LocationManagementPage')
)
const MedicalConditionsPage = lazy(
  () => import('@/features/content-management/pages/MedicalConditionsPage')
)

// Notifications pages
const NotificationsDashboardPage = lazy(
  () => import('@/features/notifications/pages/NotificationsDashboardPage')
)
const BroadcastNotificationPage = lazy(
  () => import('@/features/notifications/pages/BroadcastNotificationPage')
)
const ScheduledNotificationsPage = lazy(
  () => import('@/features/notifications/pages/ScheduledNotificationsPage')
)
const DeliveryStatusPage = lazy(
  () => import('@/features/notifications/pages/DeliveryStatusPage')
)
const PushNotificationPage = lazy(
  () => import('@/features/push-notifications/pages/PushNotificationPage')
)
const ScheduleJobsPage = lazy(
  () => import('@/features/schedule/pages/ScheduleJobsPage')
)
const TokenManagementPage = lazy(
  () => import('@/features/token/pages/TokenManagementPage')
)

// Social Features pages
const PostModerationPage = lazy(
  () => import('@/features/social/pages/PostModerationPage')
)
const CharityCampaignsPage = lazy(
  () => import('@/features/social/pages/CharityCampaignsPage')
)

// Agent Management pages
const AgentModelsPage = lazy(
  () => import('@/features/agent-management/pages/AgentModelsPage')
)
const AgentSettingsPage = lazy(
  () => import('@/features/agent-management/pages/AgentSettingsPage')
)
const AgentModelDetailPage = lazy(
  () => import('@/features/agent-management/pages/AgentModelDetailPage')
)
const AgentSettingDetailPage = lazy(
  () => import('@/features/agent-management/pages/AgentSettingDetailPage')
)

// Whitelist Management pages
const WhitelistPage = lazy(
  () => import('@/features/whitelist-management/pages/WhitelistPage')
)

// Waitlist Management pages
const WaitlistPage = lazy(
  () => import('@/features/waitlist/pages/WaitlistPage')
)

// Education Management pages
const HospitalsListPage = lazy(
  () => import('@/features/hospitals/pages/HospitalsListPage')
)

// Doctor Management pages
const DoctorListPage = lazy(
  () => import('@/features/doctors/pages/DoctorListPage')
)
const DoctorCreatePage = lazy(
  () => import('@/features/doctors/pages/DoctorCreatePage')
)
const DoctorDetailPage = lazy(
  () => import('@/features/doctors/pages/DoctorDetailPage')
)

// K-Doctor portal pages
const KdAccountListPage = lazy(
  () => import('@/features/kdoctor/pages/KdAccountListPage')
)
const KdAccountCreatePage = lazy(
  () => import('@/features/kdoctor/pages/KdAccountCreatePage')
)
const KdAccountDetailPage = lazy(
  () => import('@/features/kdoctor/pages/KdAccountDetailPage')
)
const KdDepartmentListPage = lazy(
  () => import('@/features/kdoctor/pages/KdDepartmentListPage')
)
const KdScribeDemoPage = lazy(
  () => import('@/features/kdoctor/pages/KdScribeDemoPage')
)
const KdScribeLabPage = lazy(
  () => import('@/features/kdoctor/pages/KdScribeLabPage')
)
const KdInteractionPage = lazy(
  () => import('@/features/kdoctor/pages/KdInteractionPage')
)
const KbizOrgListPage = lazy(
  () => import('@/features/kbiz/pages/KbizOrgListPage')
)
const KbizPackagesPage = lazy(
  () => import('@/features/kbiz/pages/KbizPackagesPage')
)
const KbizOrgDetailPage = lazy(
  () => import('@/features/kbiz/pages/KbizOrgDetailPage')
)
const KbizFeedbacksPage = lazy(
  () => import('@/features/kbiz/pages/KbizFeedbacksPage')
)
const EduCategoriesListPage = lazy(
  () => import('@/features/education/pages/CategoriesListPage')
)
const EduContentsListPage = lazy(
  () => import('@/features/education/pages/ContentsListPage')
)
const EduContentFormPage = lazy(
  () => import('@/features/education/pages/ContentFormPage')
)
const EduQuizzesListPage = lazy(
  () => import('@/features/education/pages/QuizzesListPage')
)
const EduQuizFormPage = lazy(
  () => import('@/features/education/pages/QuizFormPage')
)
const EduCommentsPage = lazy(
  () => import('@/features/education/pages/CommentsPage')
)
const EduCommentBansPage = lazy(
  () => import('@/features/education/pages/CommentBansPage')
)
const EduCommentBlockedWordsPage = lazy(
  () => import('@/features/education/pages/CommentBlockedWordsPage')
)
const EduExpertsListPage = lazy(
  () => import('@/features/education/pages/ExpertsListPage')
)
const EduTagsListPage = lazy(
  () => import('@/features/education/pages/TagsListPage')
)
const EduSeriesListPage = lazy(
  () => import('@/features/education/pages/SeriesListPage')
)
const EduSeriesFormPage = lazy(
  () => import('@/features/education/pages/SeriesFormPage')
)
const EduJourneysListPage = lazy(
  () => import('@/features/education/pages/JourneysListPage')
)
const EduJourneyFormPage = lazy(
  () => import('@/features/education/pages/JourneyFormPage')
)
const EduFeedPreviewPage = lazy(
  () => import('@/features/education/pages/FeedPreviewPage')
)

// Onboarding Management pages
const OnboardingListPage = lazy(
  () => import('@/features/onboarding/pages/OnboardingListPage')
)

// FAQ Management pages
const FaqCategoriesPage = lazy(
  () => import('@/features/faq-management/pages/FaqCategoriesPage')
)
const FaqItemsPage = lazy(
  () => import('@/features/faq-management/pages/FaqItemsPage')
)
const SupportQuestionsPage = lazy(
  () => import('@/features/faq-management/pages/SupportQuestionsPage')
)
const UserFeedbackPage = lazy(
  () => import('@/features/faq-management/pages/UserFeedbackPage')
)

// SOS Management pages
const SosDashboardPage = lazy(
  () => import('@/features/sos-management/pages/SosDashboardPage')
)
const SosEventsPage = lazy(
  () => import('@/features/sos-management/pages/SosEventsPage')
)
const SosEventDetailPage = lazy(
  () => import('@/features/sos-management/pages/SosEventDetailPage')
)
const SosNotificationLogsPage = lazy(
  () => import('@/features/sos-management/pages/SosNotificationLogsPage')
)
const FirstAidCmsPage = lazy(
  () => import('@/features/sos-management/pages/FirstAidCmsPage')
)
const FirstAidFormPage = lazy(
  () => import('@/features/sos-management/pages/FirstAidFormPage')
)
const FirstAidCategoriesPage = lazy(
  () => import('@/features/sos-management/pages/FirstAidCategoriesPage')
)

// Error pages
const NotFoundPage = lazy(() => import('@/features/error/pages/NotFoundPage'))
const ForbiddenPage = lazy(() => import('@/features/error/pages/ForbiddenPage'))

// RBAC pages
const RbacGroupsListPage = lazy(
  () => import('@/features/admin-management/rbac/pages/GroupsListPage')
)
const RbacGroupEditPage = lazy(
  () => import('@/features/admin-management/rbac/pages/GroupEditPage')
)
const RbacAdminAssignmentPage = lazy(
  () => import('@/features/admin-management/rbac/pages/AdminAssignmentPage')
)

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner size="lg" label="Đang tải..." />
  </div>
)

const appRoutes = [
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RootLanding />
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Home',
    },
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            {/* <DashboardPageOptimized /> */}
            <DashboardPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tổng quan',
      title: 'Tổng quan',
    },
  },
  // Whitelist Management route
  {
    path: '/whitelist',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <WhitelistPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Whitelist',
      title: 'Quản lý Whitelist',
    },
  },
  // Waitlist Management route
  {
    path: '/waitlist-registrations',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <WaitlistPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Waitlist',
      title: 'Quản lý Waitlist',
    },
  },
  {
    path: '/user-management/users',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UserManagementUsersListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh sách người dùng',
      title: 'Quản lý người dùng',
    },
  },
  {
    path: '/user-management/users/create',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="USERS_CREATE">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UserManagementCreateUserPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo người dùng mới',
      title: 'Tạo người dùng mới',
    },
  },
  {
    path: '/user-management/users/:id/edit',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="USERS_UPDATE">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UserManagementEditUserPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chỉnh sửa người dùng',
      title: 'Chỉnh sửa người dùng',
    },
  },
  {
    path: '/user-management/users/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UserManagementUserDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết người dùng',
      title: 'Chi tiết người dùng',
    },
  },
  {
    path: '/user-management/admins',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AdminManagementAdminsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh sách quản trị viên',
      title: 'Quản lý quản trị viên',
    },
  },
  {
    path: '/user-management/feedbacks',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UserManagementFeedbacksListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Phản hồi',
      title: 'Quản lý phản hồi',
    },
  },
  {
    path: '/payments',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="SUBSCRIPTIONS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <PaymentsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Thanh toán',
      title: 'Quản lý thanh toán',
    },
  },
  // Payment Subscriptions
  {
    path: '/payment-subscriptions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SubscriptionListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tra cứu Đăng ký',
      title: 'Tra cứu Đăng ký',
    },
  },
  {
    path: '/tammi-payments',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TammiOrdersPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Giao dịch Tammi',
      title: 'Giao dịch Tammi',
    },
  },
  {
    path: '/analytics',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_ANALYTICS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <div className="p-6">
                <h1 className="text-2xl font-bold">Phân tích</h1>
                <p className="text-gray-600 mt-2">
                  Trang phân tích đang được phát triển...
                </p>
              </div>
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Phân tích',
      title: 'Phân tích',
    },
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SettingsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cài đặt',
      title: 'Cài đặt tài khoản',
    },
  },
  {
    path: '/gamification/missions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <MissionDefinitionsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh sách nhiệm vụ',
      title: 'Danh sách nhiệm vụ',
    },
  },
  {
    path: '/gamification/missions/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <MissionDefinitionCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo nhiệm vụ mới',
      title: 'Tạo nhiệm vụ mới',
    },
  },
  {
    path: '/gamification/missions/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <MissionDefinitionDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết nhiệm vụ',
      title: 'Chi tiết nhiệm vụ',
    },
  },
  {
    path: '/gamification/user-monitoring',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UserMonitoringPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Giám sát Người dùng',
      title: 'Giám sát hoạt động GameFi',
    },
  },
  {
    path: '/gamification/daily-streak-rewards-config',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <DailyStreakRewardsConfigPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cấu hình Streak hàng ngày',
      title: 'Cấu hình thưởng Streak hàng ngày',
    },
  },
  {
    path: '/gamification/app-events',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppEventsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Sự kiện trong app',
      title: 'Sự kiện trong app',
    },
  },
  {
    path: '/gamification/app-events/create',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="APP_EVENTS_CREATE">
          <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppEventFormPage />
          </Suspense>
        </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo sự kiện',
      title: 'Tạo sự kiện',
    },
  },
  {
    path: '/gamification/app-events/:id/edit',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="APP_EVENTS_UPDATE">
          <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppEventFormPage />
          </Suspense>
        </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Sửa sự kiện',
      title: 'Sửa sự kiện',
    },
  },
  {
    path: '/gamification/app-events/:id/stats',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppEventStatsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Thống kê sự kiện',
      title: 'Thống kê sự kiện',
    },
  },
  {
    path: '/gamification/shop/categories',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ShopCategoriesPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Shop - Danh mục',
      title: 'Shop - Danh mục',
    },
  },
  {
    path: '/gamification/shop/products',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ShopProductsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Shop - Sản phẩm',
      title: 'Shop - Sản phẩm',
    },
  },
  {
    path: '/gamification/shop/products/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ShopProductCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo sản phẩm',
      title: 'Tạo sản phẩm',
    },
  },
  {
    path: '/gamification/shop/products/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ShopProductDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết sản phẩm',
      title: 'Chi tiết sản phẩm',
    },
  },
  {
    path: '/gamification/shop/products/:id/edit',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ShopProductEditRedirect />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chỉnh sửa sản phẩm',
      title: 'Chỉnh sửa sản phẩm',
    },
  },
  // Voucher do QLBH (HTVHKD) quản lý, CMS không quản lý voucher nữa.
  // Giữ lại VouchersPage và route để bật lại nhanh khi cần.
  // {
  //   path: '/gamification/vouchers',
  //   element: (
  //     <ProtectedRoute>
  //       <MainLayout>
  //         <Suspense fallback={<LoadingFallback />}>
  //           <VouchersPage />
  //         </Suspense>
  //       </MainLayout>
  //     </ProtectedRoute>
  //   ),
  //   handle: {
  //     breadcrumb: 'Voucher - Mã voucher',
  //     title: 'Voucher - Mã voucher',
  //   },
  // },
  {
    path: '/gamification/redemptions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <RedemptionPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Lịch sử đổi quà',
      title: 'Lịch sử đổi quà',
    },
  },
  {
    path: '/analytics/events',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <PostHogEventsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'PostHog Event Logs',
      title: 'PostHog Event Logs',
    },
  },
  {
    path: '/gamification/game-items',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <GameItemsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Game Items - Vật phẩm game',
      title: 'Vật phẩm Game',
    },
  },
  {
    path: '/gamification/tree-care-action-configs',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TreeCareActionConfigsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tree Care - Hành động chăm sóc',
      title: 'Tree Care - Hành động chăm sóc',
    },
  },
  {
    path: '/gamification/tree-growth-stage-configs',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TreeGrowthStageConfigsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tree Growth - Giai đoạn phát triển',
      title: 'Tree Growth - Giai đoạn phát triển',
    },
  },
  {
    path: '/gamification/tree-harvest-rewards-configs',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TreeHarvestRewardsConfigsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tree Harvest - Thưởng thu hoạch',
      title: 'Tree Harvest - Thưởng thu hoạch',
    },
  },
  {
    path: '/gamification/mission-item-rewards-configs',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <MissionItemRewardsConfigsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Mission Item Rewards - Thưởng vật phẩm nhiệm vụ',
      title: 'Thưởng vật phẩm nhiệm vụ',
    },
  },
  {
    path: '/surveys/topics',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyTopicsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh sách chủ đề khảo sát',
      title: 'Quản lý chủ đề khảo sát',
    },
  },
  {
    path: '/surveys/topics/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyTopicCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo chủ đề khảo sát',
      title: 'Tạo chủ đề khảo sát',
    },
  },
  {
    path: '/surveys/topics/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyTopicDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết chủ đề khảo sát',
      title: 'Chi tiết chủ đề khảo sát',
    },
  },
  {
    path: '/surveys/questions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyQuestionsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh sách câu hỏi khảo sát',
      title: 'Quản lý câu hỏi khảo sát',
    },
  },
  {
    path: '/surveys/questions/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyQuestionCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo câu hỏi khảo sát',
      title: 'Tạo câu hỏi khảo sát',
    },
  },
  {
    path: '/surveys/questions/:topicId/:questionId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SurveyQuestionDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết câu hỏi khảo sát',
      title: 'Chi tiết câu hỏi khảo sát',
    },
  },
  // Hospital Management route
  {
    path: '/hospitals',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <HospitalsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Bệnh viện',
      title: 'Quản lý Bệnh viện',
    },
  },
  // Doctor Management routes
  {
    path: '/doctors',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <DoctorListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Bác sĩ',
      title: 'Quản lý Bác sĩ',
    },
  },
  {
    path: '/doctors/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <DoctorCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Thêm bác sĩ mới',
      title: 'Thêm bác sĩ mới',
    },
  },
  {
    path: '/doctors/:doctorId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <DoctorDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết bác sĩ',
      title: 'Chi tiết bác sĩ',
    },
  },
  // K-Biz (doanh nghiệp)
  {
    path: '/kbiz/organizations',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KbizOrgListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Doanh nghiệp K-Biz',
      title: 'Doanh nghiệp K-Biz',
    },
  },
  {
    path: '/kbiz/packages',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KbizPackagesPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh mục gói K-Biz',
      title: 'Danh mục gói K-Biz',
    },
  },
  {
    path: '/kbiz/feedbacks',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KbizFeedbacksPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Đánh giá đợt khám',
      title: 'Đánh giá đợt khám',
    },
  },
  {
    path: '/kbiz/organizations/:orgId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KbizOrgDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết doanh nghiệp K-Biz',
      title: 'Chi tiết doanh nghiệp K-Biz',
    },
  },
  // K-Doctor portal routes
  {
    path: '/kdoctor/accounts',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdAccountListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tài khoản K-Doctor',
      title: 'Tài khoản K-Doctor',
    },
  },
  {
    path: '/kdoctor/accounts/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdAccountCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cấp tài khoản K-Doctor',
      title: 'Cấp tài khoản K-Doctor',
    },
  },
  {
    path: '/kdoctor/accounts/:accountId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdAccountDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết tài khoản K-Doctor',
      title: 'Chi tiết tài khoản K-Doctor',
    },
  },
  {
    path: '/kdoctor/departments',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdDepartmentListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Khoa/phòng K-Doctor',
      title: 'Khoa/phòng K-Doctor',
    },
  },
  {
    path: '/kdoctor/interactions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdInteractionPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Dữ liệu tương tác thuốc',
      title: 'Dữ liệu tương tác thuốc',
    },
  },
  {
    path: '/kdoctor/scribe-demos',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdScribeDemoPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Demo Thư ký y khoa',
      title: 'Demo Thư ký y khoa',
    },
  },
  {
    path: '/kdoctor/scribe-lab',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <KdScribeLabPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Thử nghiệm Thư ký y khoa',
      title: 'Thử nghiệm Thư ký y khoa',
    },
  },
  // Education Management routes
  {
    path: '/education/categories',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduCategoriesListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Chủ đề',
      title: 'Quản lý chủ đề giáo dục',
    },
  },
  {
    path: '/education/contents',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduContentsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Nội dung',
      title: 'Quản lý nội dung giáo dục',
    },
  },
  {
    path: '/education/contents/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduContentFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo nội dung mới',
      title: 'Tạo nội dung giáo dục',
    },
  },
  {
    path: '/education/contents/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduContentFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết nội dung',
      title: 'Chi tiết nội dung giáo dục',
    },
  },
  {
    path: '/education/quizzes',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduQuizzesListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Câu hỏi',
      title: 'Quản lý câu hỏi giáo dục',
    },
  },
  {
    path: '/education/quizzes/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduQuizFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo câu hỏi mới',
      title: 'Tạo câu hỏi giáo dục',
    },
  },
  {
    path: '/education/quizzes/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduQuizFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết câu hỏi',
      title: 'Chi tiết câu hỏi giáo dục',
    },
  },
  // Video Tri thức: chuyên gia, tag đối tượng, series, xem trước feed
  {
    path: '/education/experts',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduExpertsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chuyên gia',
      title: 'Chuyên gia',
    },
  },
  {
    path: '/education/tags',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduTagsListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Mục tiêu & đối tượng',
      title: 'Mục tiêu & đối tượng',
    },
  },
  {
    path: '/education/series',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduSeriesListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Series',
      title: 'Series',
    },
  },
  {
    path: '/education/series/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduSeriesFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo series',
      title: 'Tạo series',
    },
  },
  {
    path: '/education/series/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduSeriesFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Sửa series',
      title: 'Sửa series',
    },
  },
  {
    path: '/education/journeys',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduJourneysListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Lộ trình',
      title: 'Lộ trình',
    },
  },
  {
    path: '/education/journeys/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduJourneyFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo lộ trình',
      title: 'Tạo lộ trình',
    },
  },
  {
    path: '/education/journeys/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduJourneyFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Sửa lộ trình',
      title: 'Sửa lộ trình',
    },
  },
  {
    path: '/education/feed-preview',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EduFeedPreviewPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Xem trước feed',
      title: 'Xem trước feed',
    },
  },
  // Bình luận bài viết (edu)
  {
    path: '/education/comments',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="EDU_COMMENT_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <EduCommentsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Bình luận bài viết',
      title: 'Bình luận bài viết',
    },
  },
  {
    path: '/education/comments/bans',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="EDU_COMMENT_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <EduCommentBansPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Người bị chặn bình luận',
      title: 'Người bị chặn bình luận',
    },
  },
  {
    path: '/education/comments/blocked-words',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="EDU_COMMENT_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <EduCommentBlockedWordsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Từ cấm trong bình luận',
      title: 'Từ cấm trong bình luận',
    },
  },
  // Onboarding Management routes
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingListPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Onboarding',
      title: 'Quản lý màn hình Onboarding',
    },
  },
  // Reports & Analytics routes
  {
    path: '/reports/bi-dashboard',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_BI_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <BIDashboardPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Bảng điều khiển BI',
      title: 'Bảng điều khiển BI',
    },
  },
  {
    path: '/reports/custom',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_CUSTOM_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CustomReportsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Báo cáo tùy chỉnh',
      title: 'Báo cáo tùy chỉnh',
    },
  },
  {
    path: '/reports/scheduled',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_SCHEDULED_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ScheduledReportsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Báo cáo định kỳ',
      title: 'Báo cáo định kỳ',
    },
  },
  {
    path: '/reports/analytics',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_ANALYTICS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <AnalyticsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Phân tích',
      title: 'Phân tích & Thống kê',
    },
  },
  {
    path: '/reports/predictive',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="REPORTS_PREDICTIVE_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <PredictiveAnalyticsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Dự đoán',
      title: 'Phân tích dự đoán',
    },
  },
  // System Configuration routes
  {
    path: '/system-config',
    element: (
      <ProtectedRoute>
        <RouteGuard anyOf={['SYS_CONFIG_VIEW', 'APP_CONFIG_VIEW', 'VOICE_CONFIG_VIEW']}>
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <SystemConfigIndexPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cấu hình hệ thống',
      title: 'Cấu hình hệ thống',
    },
  },
  // Feature Flags route - commented out (not supported by API)
  // {
  //   path: '/system-config/feature-flags',
  //   element: (
  //     <ProtectedRoute>
  //       <MainLayout>
  //         <Suspense fallback={<LoadingFallback />}>
  //           <FeatureFlagsPage />
  //         </Suspense>
  //       </MainLayout>
  //     </ProtectedRoute>
  //   ),
  //   handle: {
  //     breadcrumb: 'Feature Flags',
  //     title: 'Feature Flags Management',
  //   },
  // },
  {
    path: '/system-config/app-settings',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppSettingsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cài đặt ứng dụng',
      title: 'Cài đặt ứng dụng',
    },
  },
  {
    path: '/system-config/app-settings/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppSettingCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo cài đặt mới',
      title: 'Tạo cài đặt mới',
    },
  },
  {
    path: '/system-config/app-settings/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AppSettingDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết cài đặt',
      title: 'Chi tiết cài đặt',
    },
  },
  {
    path: '/system-config/notification-templates',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="SYS_CONFIG_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <NotificationTemplatesPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Mẫu thông báo',
      title: 'Quản lý mẫu thông báo',
    },
  },
  {
    path: '/system-config/localization',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="SYS_CONFIG_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <LocalizationKeysPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Đa ngôn ngữ',
      title: 'Quản lý đa ngôn ngữ',
    },
  },
  {
    path: '/system-config/app-configs',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SystemAppConfigsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cấu hình chung',
      title: 'Cấu hình chung',
    },
  },
  {
    path: '/system-config/tts-voices',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TTSVoicesConfigPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cấu hình giọng nói',
      title: 'Cấu hình giọng nói',
    },
  },
  {
    path: '/system-config/tts-voices/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TTSVoiceCreatePage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo giọng nói mới',
      title: 'Tạo giọng nói mới',
    },
  },
  {
    path: '/system-config/tts-voices/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TTSVoiceDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết giọng nói',
      title: 'Chi tiết giọng nói',
    },
  },
  // Maintenance Mode route - commented out (not supported by API)
  // {
  //   path: '/system-config/maintenance',
  //   element: (
  //     <ProtectedRoute>
  //       <MainLayout>
  //         <Suspense fallback={<LoadingFallback />}>
  //           <MaintenanceModePage />
  //         </Suspense>
  //       </MainLayout>
  //     </ProtectedRoute>
  //   ),
  //   handle: {
  //     breadcrumb: 'Maintenance Mode',
  //     title: 'Maintenance Mode',
  //   },
  // },
  // FAQ Management routes
  {
    path: '/faq/categories',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FaqCategoriesPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh mục FAQ',
      title: 'Danh mục FAQ',
    },
  },
  {
    path: '/faq/items',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FaqItemsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Câu hỏi FAQ',
      title: 'Câu hỏi FAQ',
    },
  },
  {
    path: '/support/questions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SupportQuestionsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Câu hỏi hỗ trợ',
      title: 'Câu hỏi hỗ trợ',
    },
  },
  {
    path: '/feedback',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UserFeedbackPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Phản hồi người dùng',
      title: 'Phản hồi người dùng',
    },
  },
  // Agent Management routes
  {
    path: '/agent-management/models',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentModelsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Mô hình Agent',
      title: 'Quản lý mô hình Agent',
    },
  },
  {
    path: '/agent-management/models/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentModelDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo Agent Model mới',
      title: 'Tạo mô hình Agent',
    },
  },
  {
    path: '/agent-management/models/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentModelDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết Agent Model',
      title: 'Chi tiết mô hình Agent',
    },
  },
  {
    path: '/agent-management/settings',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentSettingsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Cài đặt Agent',
      title: 'Quản lý cài đặt Agent',
    },
  },
  {
    path: '/agent-management/settings/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentSettingDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo Agent Setting mới',
      title: 'Tạo cài đặt Agent',
    },
  },
  {
    path: '/agent-management/settings/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <AgentSettingDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết Agent Setting',
      title: 'Chi tiết cài đặt Agent',
    },
  },
  // Content Management routes
  {
    path: '/content/legal-documents',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <LegalDocumentsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tài liệu pháp lý',
      title: 'Quản lý tài liệu pháp lý',
    },
  },
  {
    path: '/content/locations',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <LocationManagementPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Địa điểm',
      title: 'Quản lý địa điểm',
    },
  },
  {
    path: '/content/medical-conditions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <MedicalConditionsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Bệnh nền',
      title: 'Quản lý bệnh nền',
    },
  },
  // Notifications routes
  {
    path: '/notifications',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="NOTIFICATIONS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <NotificationsDashboardPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tổng quan thông báo',
      title: 'Quản lý thông báo',
    },
  },
  {
    path: '/notifications/broadcast',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="NOTIFICATIONS_BROADCAST">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <BroadcastNotificationPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Gửi thông báo',
      title: 'Gửi thông báo hàng loạt',
    },
  },
  {
    path: '/notifications/scheduled',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="NOTIFICATIONS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ScheduledNotificationsPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Đã lên lịch',
      title: 'Thông báo đã lên lịch',
    },
  },
  {
    path: '/notifications/delivery-status',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="NOTIFICATIONS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <DeliveryStatusPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Trạng thái gửi',
      title: 'Trạng thái gửi thông báo',
    },
  },
  {
    path: '/notifications/push',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <PushNotificationPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Push Notification (FCM)',
      title: 'Quản lý thông báo đẩy (FCM)',
    },
  },
  {
    path: '/schedule',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <ScheduleJobsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý lịch trình',
      title: 'Quản lý lịch trình',
    },
  },
  {
    path: '/tokens',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TokenManagementPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Quản lý Token',
      title: 'Quản lý Token',
    },
  },
  // Social Features routes
  {
    path: '/social/posts',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <PostModerationPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Kiểm duyệt bài viết',
      title: 'Kiểm duyệt bài viết',
    },
  },
  {
    path: '/social/charity',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <CharityCampaignsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chiến dịch từ thiện',
      title: 'Quản lý chiến dịch từ thiện',
    },
  },
  // SOS Management routes
  {
    path: '/sos',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SosDashboardPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tổng quan SOS',
      title: 'Quản lý SOS khẩn cấp',
    },
  },
  {
    path: '/sos/events',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SosEventsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Sự kiện SOS',
      title: 'Quản lý sự kiện SOS',
    },
  },
  {
    path: '/sos/events/:eventId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SosEventDetailPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết sự kiện SOS',
      title: 'Chi tiết sự kiện SOS',
    },
  },
  {
    path: '/sos/notifications',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <SosNotificationLogsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Nhật ký thông báo',
      title: 'Nhật ký thông báo SOS',
    },
  },
  {
    path: '/sos/first-aid',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FirstAidCmsPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Nội dung sơ cứu',
      title: 'Quản lý nội dung sơ cứu',
    },
  },
  {
    path: '/sos/first-aid/categories',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FirstAidCategoriesPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Danh mục sơ cứu',
      title: 'Quản lý danh mục sơ cứu',
    },
  },
  {
    path: '/sos/first-aid/create',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FirstAidFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo hướng dẫn mới',
      title: 'Tạo nội dung sơ cứu',
    },
  },
  {
    path: '/sos/first-aid/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FirstAidFormPage />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chỉnh sửa hướng dẫn',
      title: 'Chỉnh sửa nội dung sơ cứu',
    },
  },
  // Admin RBAC — SuperAdmin-only screens (guarded by PERMISSION_GROUPS_VIEW)
  {
    path: '/admin-management/rbac/groups',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="PERMISSION_GROUPS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RbacGroupsListPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Nhóm quyền',
      title: 'Quản lý nhóm quyền',
    },
  },
  {
    path: '/admin-management/rbac/groups/new',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="PERMISSION_GROUPS_CREATE">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RbacGroupEditPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Tạo nhóm quyền',
      title: 'Tạo nhóm quyền',
    },
  },
  {
    path: '/admin-management/rbac/groups/:id',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="PERMISSION_GROUPS_UPDATE">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RbacGroupEditPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chỉnh sửa nhóm quyền',
      title: 'Chỉnh sửa nhóm quyền',
    },
  },
  {
    path: '/admin-management/rbac/groups/:id/view',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="PERMISSION_GROUPS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RbacGroupEditPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Chi tiết nhóm quyền',
      title: 'Chi tiết nhóm quyền',
    },
  },
  {
    path: '/admin-management/rbac/admins/:adminId',
    element: (
      <ProtectedRoute>
        <RouteGuard requiredPermission="ADMIN_USERS_VIEW">
          <MainLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RbacAdminAssignmentPage />
            </Suspense>
          </MainLayout>
        </RouteGuard>
      </ProtectedRoute>
    ),
    handle: {
      breadcrumb: 'Phân quyền quản trị viên',
      title: 'Phân quyền quản trị viên',
    },
  },
  // 403 Forbidden
  {
    path: '/403',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ForbiddenPage />
      </Suspense>
    ),
    handle: {
      breadcrumb: '403 - Không đủ quyền',
      title: '403 - Không đủ quyền',
    },
  },
  // Trang chỉ có khi chạy dev (bản build loại hẳn module này)
  ...(import.meta.env.DEV ? devRoutes() : []),
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
    handle: {
      breadcrumb: '404 - Không tìm thấy trang',
      title: '404 - Không tìm thấy trang',
    },
  },
]

export const router = createBrowserRouter(
  appRoutes.map((route) => ({
    errorElement: <RouteErrorBoundary />,
    ...route,
  }))
)
